import { injectable } from 'inversify';
import { Observable, Subject } from 'rxjs';

import { ClientError } from '../utils/request.utils';

/**
 * Normalized enforcement / billing error shape that drives the
 * `<EnforcementErrorModal/>`. Source can be either:
 *
 *  - The legacy `enforcement_error` JSON envelope returned on HTTP 429
 *    by `/v1/api/servers/resolve` and emitted over the WS for post-upload
 *    rejection (`dataType: enforcement_error`).
 *  - The RFC 9457 `application/problem+json` envelope returned on HTTP
 *    402 (`code: insufficient_credits`) by the Developer-API surface.
 *
 * Both shapes are flattened here so the modal renders one consistent UI.
 */
export interface EnforcementErrorDetails {
    httpStatus: number;
    reason?: string;
    title: string;
    userMessage: string;
    currentValue?: number;
    limitValue?: number;
    currentTier?: string;
    upgradeRequired: boolean;
    subscribeUrl?: string;
    topupUrl?: string;
    matchedPattern?: string;
}

interface LegacyEnforcementBody {
    error?: boolean;
    status?: number;
    dataType?: string;
    userMessage?: string;
    reason?: string;
    currentTier?: string;
    currentValue?: number;
    limitValue?: number;
    upgradeRequired?: boolean;
    matchedPattern?: string;
}

interface ProblemJsonBody {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    code?: string;
    reason?: string;
    currentBalance?: number;
    subscriptionBalance?: number;
    paygBalance?: number;
    estimatedCost?: number;
    subscribeUrl?: string;
    topupUrl?: string;
    userMessage?: string;
    currentTier?: string;
    currentValue?: number;
    limitValue?: number;
    upgradeRequired?: boolean;
    matchedPattern?: string;
}

/**
 * Global stream of enforcement/billing rejections. The
 * `<EnforcementErrorModal/>` mounted in `_app.tsx` subscribes to `event$`
 * and shows the modal whenever a new event arrives. Any service that
 * encounters a 429 `enforcement_error` or 402 `insufficient_credits`
 * (HTTP body or WS payload) calls `show(details)` and immediately
 * propagates the error to its caller — no retry.
 */
@injectable()
export class EnforcementErrorService {
    private readonly subject = new Subject<EnforcementErrorDetails>();

    get event$(): Observable<EnforcementErrorDetails> {
        return this.subject.asObservable();
    }

    show(details: EnforcementErrorDetails) {
        this.subject.next(details);
    }

    /**
     * Detect whether an arbitrary error is an enforcement or
     * insufficient-credits rejection that the global modal can present.
     * Recognizes both the legacy `enforcement_error` envelope (429) and
     * the RFC 9457 `insufficient_credits` envelope (402).
     */
    static isEnforcementError(err: unknown): boolean {
        if (!err || typeof err !== 'object') return false;
        const status = (err as { status?: number }).status;
        if (status === 429 || status === 402) {
            const body = (err as { response?: unknown }).response;
            return EnforcementErrorService.bodyLooksLikeEnforcement(body);
        }
        return false;
    }

    private static bodyLooksLikeEnforcement(body: unknown): boolean {
        if (!body || typeof body !== 'object') return false;
        const obj = body as LegacyEnforcementBody & ProblemJsonBody;
        return obj.dataType === 'enforcement_error'
            || obj.code === 'insufficient_credits';
    }

    /**
     * Extract a normalized `EnforcementErrorDetails` from any error or
     * raw body. Tolerates both envelopes; falls back to a generic
     * message when fields are sparse.
     */
    static extractDetails(err: unknown): EnforcementErrorDetails {
        let status = 429;
        let body: LegacyEnforcementBody & ProblemJsonBody = {};
        if (err && typeof err === 'object') {
            status = (err as { status?: number }).status ?? 429;
            const responseField = (err as { response?: unknown }).response;
            if (responseField && typeof responseField === 'object') {
                body = responseField as typeof body;
            } else if (typeof responseField === 'string') {
                body = EnforcementErrorService.safeJsonParse(responseField);
            } else if (!(err as ClientError).response
                && typeof (err as { message?: string }).message === 'string') {
                body = EnforcementErrorService.safeJsonParse(
                    (err as { message: string }).message,
                );
            }
        }

        return EnforcementErrorService.fromBody(body, status);
    }

    /**
     * Build details directly from a parsed body (used by the WS
     * post-upload `enforcement_error` payload, which already arrives
     * parsed). `httpStatus` defaults to 429 — the WS path is always a
     * 429-equivalent enforcement rejection.
     */
    static fromBody(
        body: LegacyEnforcementBody & ProblemJsonBody,
        httpStatus = 429,
    ): EnforcementErrorDetails {
        const reason = body.reason
            || (body.code === 'insufficient_credits' ? 'insufficient_credits' : undefined);
        const title = body.title
            || EnforcementErrorService.titleFromReason(reason, httpStatus);
        const userMessage = body.userMessage
            || body.detail
            || EnforcementErrorService.fallbackMessage(reason, body);
        const upgradeRequired = body.upgradeRequired
            ?? (httpStatus === 402);
        return {
            httpStatus,
            reason,
            title,
            userMessage,
            currentValue: body.currentValue,
            limitValue: body.limitValue,
            currentTier: body.currentTier,
            upgradeRequired,
            subscribeUrl: body.subscribeUrl,
            topupUrl: body.topupUrl,
            matchedPattern: body.matchedPattern,
        };
    }

    private static titleFromReason(reason: string | undefined, status: number): string {
        if (status === 402) return 'Insufficient credits';
        switch (reason) {
            case 'daily_limit':
            case 'primary_daily_limit':
                return 'Daily conversion limit reached';
            case 'daily_cpu_limit':
                return 'Daily processing-time limit reached';
            case 'concurrent_limit':
            case 'session_concurrent_limit':
                return 'Concurrent conversion limit reached';
            case 'ip_family_daily_limit':
                return 'Network daily limit reached';
            case 'ip_family_concurrent_limit':
                return 'Network concurrent limit reached';
            case 'no_active_subscription':
                return 'No active API subscription';
            case 'insufficient_balance':
                return 'Insufficient credit balance';
            default:
                return "You've hit a limit";
        }
    }

    private static fallbackMessage(
        reason: string | undefined,
        body: LegacyEnforcementBody & ProblemJsonBody,
    ): string {
        if (body.currentValue != null && body.limitValue != null) {
            return `Used ${body.currentValue} of ${body.limitValue}. Try again later or upgrade your plan.`;
        }
        if (reason === 'no_active_subscription') {
            return 'Your API subscription is not active. Subscribe to use the API.';
        }
        return 'Your request was rejected by usage limits. Try again later or upgrade your plan.';
    }

    private static safeJsonParse(maybeJson: string): LegacyEnforcementBody & ProblemJsonBody {
        try {
            return JSON.parse(maybeJson) as LegacyEnforcementBody & ProblemJsonBody;
        } catch {
            return {};
        }
    }
}
